/**
 * ================================================================================================
 * >>> **HER 2026-08-28 CLAUSE 2: THE TAP LANDS IN THE LIVE, NOT NEAR IT.** <<<
 *
 * Her words: *"tapping the push OR the email button lands them DIRECTLY IN THE LIVE, the watch
 * screen itself, signed-in members straight in, signed-out members through sign-in then INTO the
 * live, never the home page, never the Living Room feed to hunt."*
 *
 * The fan-out has been composing `/living-room?live=<id>` since the day it shipped and **NOTHING ON
 * THE CLIENT EVER READ THAT PARAMETER** -- R38/R165's trap: a deep link written by one side and
 * consumed by nobody is a link that lands her audience in a feed to go hunting through.
 *
 * **THIS MODULE IMPORTS NOTHING**, so a FAST fixture RUNS every law in it (R146).
 * ================================================================================================
 */

export const LIVE_PARAM = "live";
const KEY = "twelve.live.intent";

/**
 * Which live a URL is pointing at, or "".
 *
 * **PRESENCE, NEVER TRUTHINESS (R53/R136/R137).** This comes off a query string, where everything is
 * a string and `Boolean("0")` is true. Only a non-empty trimmed value is an id, and anything else --
 * a missing param, an empty one, a search that is not a string -- is NOT a live intent.
 */
export function liveIdFromSearch(search) {
  if (typeof search !== "string" || !search) return "";
  const m = /[?&]live=([^&#]+)/.exec(search);
  if (!m) return "";
  try { return decodeURIComponent(m[1]).trim(); } catch { return String(m[1]).trim(); }
}

/**
 * >>> **THE STASH, AND WHY IT IS `sessionStorage` -- `talkDoor.js`'s OWN RECORDED REASON.** <<<
 *
 * A signed-out member taps the push, meets the sign-in door, and on the Google path leaves this
 * origin entirely and comes back. State does not survive that; `sessionStorage` does, and it starts
 * clean in a new session -- where a `localStorage` stash would drop somebody into a live next week
 * because a stranger tapped a link on this device today.
 *
 * **EVERY READ AND WRITE IS THROW-SAFE**, because a storage that refuses is a browser setting rather
 * than a fact about her arrival, and a throw here is a white screen on a link her audience was
 * invited through (R19: a crash is not a refusal).
 */
export function writeLiveIntent(store, id) {
  const clean = typeof id === "string" ? id.trim() : "";
  if (!clean) return false;
  try { store?.setItem?.(KEY, clean); return true; } catch { return false; }
}

export function readLiveIntent(store) {
  try {
    const v = store?.getItem?.(KEY);
    return typeof v === "string" ? v.trim() : "";
  } catch { return ""; }
}

/** Spent the moment it is used: a landing intent that outlives its landing is a diversion. */
export function clearLiveIntent(store) {
  try { store?.removeItem?.(KEY); return true; } catch { return false; }
}

/**
 * Which live should this screen open, given the URL and the stash?
 *
 * THE URL WINS, because it is the tap that just happened; the stash is the one she carried through a
 * sign-in. One reader, so the rail and a gate cannot disagree (R28/R29).
 */
export function liveToOpen({ search = "", stashed = "" } = {}) {
  return liveIdFromSearch(search) || (typeof stashed === "string" ? stashed.trim() : "");
}

/** The address with the parameter taken off, so a refresh does not re-open a live she has left. */
export function addressWithoutLive(search, pathname = "/") {
  if (typeof search !== "string" || !search) return "";
  const kept = search.replace(/^\?/, "").split("&").filter((p) => p && !/^live=/.test(p));
  return `${pathname}${kept.length ? `?${kept.join("&")}` : ""}`;
}
